import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAppContext } from '@/composables/useAppContext';
import { useIssueCategoryChart } from '@/charts/configs/useIssueCategoryChart';
import { usePriorityChart } from '@/charts/configs/usePriorityChart';
import { useResponsiblePartyDonutChart } from '@/charts/configs/useResponsiblePartyDonutChart';
import { useAssignedTeamDonutChart } from '@/charts/configs/useAssignedTeamDonutChart';
import { useControllerDonutChart } from '@/charts/configs/useControllerDonutChart';
import { useCreatedByDonutChart } from '@/charts/configs/useCreatedByDonutChart';
import { useIssueBurndownChart } from '@/charts/configs/useIssueBurndownChart';
import { useIssueVelocityChart } from '@/charts/configs/useIssueVelocityChart';
import { useResolutionTimeChart } from '@/charts/configs/useResolutionTimeChart';
import { useIssuesGrid } from '@/composables/useIssuesGrid';

export default () => {
  const store = useStore();
  const app = useAppContext();
  const settings = app.settings;
  const locale = app.locale;

  const conn = app.conn;
  const dataLoaded = ref(false);

  const filterStatusHistoryContext = app.filterStatusHistoryContext;
  const filterContext = app.filterIssuesContext;
  const chartFontColor = app.chartFontColor;
  // The bar charts and the donuts load on the GLOBAL data-loaded flag (what their old wrappers read
  // from the store), not this page's own `dataLoaded`, which only flips after the date-range query
  // below (it still mounts the donuts' cards).
  const issuesLoaded = app.dataLoaded;
  // The issues grid (the package IssuesGrid since 2026-10-12), on the GLOBAL flag too: its data,
  // report and link opener are useIssuesGrid's — one bag of props
  const issuesGrid = useIssuesGrid({
    conn,
    filterContext,
    dataLoaded: issuesLoaded,
    settings,
    buildMode: app.buildMode,
    logoUrl: app.logoUrl,
    duckdb: app.duckdb,
  });

  const priorityChart = usePriorityChart({ chartName: 'priorityStackedBarChart', filterContext, conn });
  const categoryChart = useIssueCategoryChart({ chartName: 'categoryStackedBarChart', filterContext, conn });
  // The four donuts (package BaseDonutChart, `bare` inside this page's two tabbed cards)
  const responsiblePartyDonut = useResponsiblePartyDonutChart({ chartName: 'responsiblePartyDonutChart', filterContext, conn });
  const assignedTeamDonut = useAssignedTeamDonutChart({ chartName: 'assignedTeamDonutChart', filterContext, conn });
  const controllerDonut = useControllerDonutChart({ chartName: 'controllerTeamDonutChart', filterContext, conn });
  const createdByDonut = useCreatedByDonutChart({ chartName: 'createdByTeamDonutChart', filterContext, conn });
  // The two time series (package BaseTimeSeriesChart) and the Resolution Time box plot, on THIS
  // page's `dataLoaded` as before. All three reload on EITHER context's ticks (the burn-up's too
  // since Daniel's 2026-10-07 decision: its SQL reads both context tables; the old SFC watched the
  // status-history context only; the box plot's old base watched both) — one stable array, so a
  // re-render of this page does not hand the charts a new one. The PNG download is the web app's
  // only, as before.
  const burndownChart = useIssueBurndownChart({ conn });
  const velocityChart = useIssueVelocityChart({ conn });
  const issueAndHistoryContexts = computed(() => [filterContext.value, filterStatusHistoryContext.value]);
  const showChartDownload = computed(() => app.buildMode.value === 'webapp');
  // Resolution Time (package BaseBoxPlotChart, IA_ABSORPTION_PLAN 12d): a box click filters the
  // status-history context; the config also owns the card's status picker and date-range dialog.
  // Its first load waits for this page's `dataLoaded` AND the statuses + history dates (`ready`).
  const {
    chart: resolutionTimeChart,
    ready: resolutionTimeReady,
    color: resolutionTimeColor,
    statusOptions,
    selectedStatus,
    setStatus,
    settingsDialog,
    statusHistoryDates,
    tempStatusHistoryRange,
    getDateFromIndex,
    applySettings,
    cancelSettings,
  } = useResolutionTimeChart({ chartName: 'issueResolutionBoxChart', filterContext: filterStatusHistoryContext, conn, dataLoaded });

  const startDate = ref('');
  const endDate = ref('');
  const dateRange = ref([]);
  const minDate = ref(new Date());
  const maxDate = ref(new Date());
  const showNoActivityWarning = ref(false);

  const donutTab = ref('responsible');
  const donutTab2 = ref('controller');

  // The two tabbed donut cards' PNG export: the button in each card's tabs row exports the ACTIVE
  // tab's donut through these STRING template refs (`ref="…"` in component.vue — they only bind
  // because they are returned under these exact names). The first donut of a card is v-show (it
  // stays mounted), the second v-if (null until its tab is opened).
  const responsiblePartyDonutRef = ref(null);
  const assignedTeamDonutRef = ref(null);
  const controllerDonutRef = ref(null);
  const createdByDonutRef = ref(null);
  const downloadDonutTab = () => {
    const donut = donutTab.value === 'assigned' ? assignedTeamDonutRef.value : responsiblePartyDonutRef.value;
    donut?.downloadPng();
  };
  const downloadDonutTab2 = () => {
    const donut = donutTab2.value === 'createdBy' ? createdByDonutRef.value : controllerDonutRef.value;
    donut?.downloadPng();
  };




  const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return new Date(value);
    return null;
  };


  const formatDateForPicker = (date) => {
    const validDate = toDate(date);
    if (!validDate) return '';

    const year = validDate.getFullYear();
    const month = String(validDate.getMonth() + 1).padStart(2, '0');
    const day = String(validDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(locale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  // Initialize data on component mount only if necessary
  const initializeData = async () => {

    await store.commit('resetStatusHistoryFilters');
    await store.commit('resetFilters');

    if (settings.value.scopedToken && settings.value.projectCode) {
        // Still need to query for min and max dates even if the table exists
        try {
          const dateRangeResult = await conn.query(`
          SELECT 
            MIN(createdDate) as minDate,
            MAX(createdDate) as maxDate
          FROM status_history
        `);

          const dateRangeData = dateRangeResult.toArray();

          if (dateRangeData && dateRangeData.length > 0) {
            minDate.value = new Date(dateRangeData[0].minDate);
            maxDate.value = new Date(dateRangeData[0].maxDate);

            // Set initial date range if not already set
            if (!dateRange.value || dateRange.value.length !== 2) {
              dateRange.value = [minDate.value, maxDate.value];
              startDate.value = formatDateForPicker(minDate.value);
              endDate.value = formatDateForPicker(maxDate.value);
            }
          }

        } catch (error) {
          console.error("Error fetching date range:", error);
        }

      dataLoaded.value = true;
    }
  };

  onMounted(async () => {
    await initializeData();
  });

  return {
    dataLoaded,
    donutTab,
    responsiblePartyDonutRef,
    assignedTeamDonutRef,
    controllerDonutRef,
    createdByDonutRef,
    downloadDonutTab,
    downloadDonutTab2,
    responsiblePartyDonut,
    filterContext,
    chartFontColor,
    issuesLoaded,
    assignedTeamDonut,
    priorityChart,
    donutTab2,
    controllerDonut,
    createdByDonut,
    categoryChart,
    resolutionTimeChart,
    issueAndHistoryContexts,
    resolutionTimeReady,
    resolutionTimeColor,
    selectedStatus,
    setStatus,
    statusOptions,
    settingsDialog,
    getDateFromIndex,
    tempStatusHistoryRange,
    statusHistoryDates,
    cancelSettings,
    applySettings,
    showChartDownload,
    burndownChart,
    velocityChart,
    showNoActivityWarning,
    formatDateForDisplay,
    dateRange,
    issuesGrid
  };
};
